import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  Stack,
  TextField
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';

import {
  type MessageTemplateDTO,
  type TemplateDialogPropsType,
  useAdministrationTemplates
} from '@/pages/Administration/AdministrationTemplates';

import DEFAULT_MESSAGE_TEMPLATE_DIALOG_VALUES from './MessageTemplateDialog.constants';

const MessageTemplateDialog = (props: TemplateDialogPropsType) => {
  const { open, setOpen, templateType, selectedTemplate } = props;
  const {
    updateTemplate,
    createTemplate,
    createError,
    createSuccess,
    updateSuccess,
    updateError
  } = useAdministrationTemplates();

  const form = useForm({
    defaultValues: DEFAULT_MESSAGE_TEMPLATE_DIALOG_VALUES as MessageTemplateDTO
  });

  const { control, setValue, getValues, handleSubmit } = form;
  const message = useWatch({ control, name: 'message' }) as string;
  const templateName = useWatch({ control, name: 'template_name' });

  useEffect(() => {
    setValue('message', selectedTemplate?.message as string);
    setValue('template_name', selectedTemplate?.template_name as string);
  }, [selectedTemplate, open]);

  const handleSave = (actionId?: string) => {
    const newTemplateInfo: {
      [key: string]: string | MessageTemplateDTO | number;
    } = {};

    const templateData = {
      message: getValues('message'),
      template_name: getValues('template_name')
    };
    newTemplateInfo.templateType = 'message';
    newTemplateInfo.data = templateData;

    if (actionId === 'save') {
      newTemplateInfo.id = selectedTemplate.id as number;
      updateTemplate(
        newTemplateInfo as {
          templateType: string;
          id: number;
          data: MessageTemplateDTO;
        }
      );
    } else {
      createTemplate(
        newTemplateInfo as { templateType: string; data: MessageTemplateDTO }
      );
    }
  };

  useEffect(() => {
    if (createSuccess) {
      setOpen(false);
    } else if (updateSuccess) {
      setOpen(false);
    }
  }, [createSuccess, updateSuccess]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      fullWidth
      maxWidth="md"
      scroll="paper"
    >
      <DialogTitle>{`${templateType} Template`}</DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(handleSave as () => void)} noValidate>
          <DialogContent>
            <Stack spacing={2}>
              <div style={{ paddingTop: 10 }}>
                <InputLabel htmlFor="template-name">Template Name *</InputLabel>
                <Controller
                  control={control}
                  name="template_name"
                  rules={{
                    required: true
                  }}
                  render={({ field, formState }) => (
                    <FormControl fullWidth>
                      <TextField
                        id="template-name"
                        error={!!formState.errors.template_name}
                        {...field}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => setValue('template_name', event.target.value)}
                      />
                    </FormControl>
                  )}
                />
                {createError && typeof createError === 'string' && (
                  <Alert sx={{ mt: 1 }} severity="error">
                    {createError}
                  </Alert>
                )}
                {updateError && typeof updateError === 'string' && (
                  <Alert sx={{ mt: 1 }} severity="error">
                    {updateError}
                  </Alert>
                )}
                <Divider sx={{ marginTop: 3 }} />
              </div>
              <div>
                <InputLabel htmlFor="message">Message *</InputLabel>
                <Controller
                  control={control}
                  name="message"
                  rules={{
                    required: true
                  }}
                  render={({ field, formState }) => (
                    <FormControl fullWidth>
                      <TextField
                        id="message"
                        multiline
                        minRows={6}
                        error={!!formState.errors.message}
                        {...field}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => setValue('message', event.target.value)}
                      />
                    </FormControl>
                  )}
                />
              </div>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              {templateType.includes('New') && (
                <Button
                  disabled={
                    (message?.length ?? 0) < 5 ||
                    (templateName?.length ?? 0) < 5
                  }
                  type="submit"
                  variant="contained"
                >
                  Create Template
                </Button>
              )}
              {templateType.includes('Edit') && (
                <Button
                  disabled={
                    (message?.length ?? 0) < 5 ||
                    (templateName?.length ?? 0) < 5
                  }
                  onClick={() => handleSave('save')}
                  variant="contained"
                >
                  Save Changes
                </Button>
              )}
            </Stack>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default MessageTemplateDialog;
